/**
 * Order-flow signals from the local flow-signals service (Rithmic MBO), relayed
 * by the engine (utt-engine/src/flow_signals). Visual only: nothing here feeds a
 * bot or strategy.
 *
 * Contract (utt-engine):
 * - REST GET {api}/flow-signals/{symbol}?since_ms=&kinds=&limit= -> { symbol, signals }
 * - Socket "flow-signal" -> FlowSignal, to the symbol room the chart already joins
 * - Socket "flow-status" -> FlowStatus, broadcast and on connect
 *
 * The engine never pushes a signal older than 60s, so history arrives over REST:
 * on every symbol a chart starts watching, and again for every watched symbol
 * once per reconnect (socket back, or the engine's relay back on
 * flow-signals; both together refetch once: onFlowHistoryGap).
 *
 * Visibility and colours live in levelsStore (`flow*` keys).
 */
import { create } from 'zustand';
import { wsService } from '../services/WebSocketService';
import { getApiBaseUrl } from '../config/ports';
import { normalizeToBase } from '../utils/symbolUtils';
import { useMarketStore } from './marketStore';

/** `detail.edge` (PROTOCOL.md): the self-tuner's reading for the signal's kind and root. */
export interface FlowEdge {
  reading: string;
  /** Mean t over the 60..300 s horizons; absent in tuner files that predate it. */
  band_t?: number;
  /** 60..300 s horizon with the largest |t| of band_t's sign (informational). */
  best_horizon_s?: number;
  /** Mean forward ticks by horizon in seconds ("60" .. "300"). */
  mean_ticks?: Record<string, number>;
  /** 300 s mean and t, kept for older clients. */
  mean_ticks_300s: number;
  t: number;
}

/**
 * |t| below this = no reliable edge. PROTOCOL.md: "|band_t| below ~2 = no
 * reliable edge" (flow-signals' EDGE_T_RELIABLE). The one definition: the
 * Settings tab's edge table and every readout use it.
 */
export const EDGE_T_RELIABLE = 2.0;

export interface FlowSignal {
  /** Stable: contract, kind, ts_ns, price_ticks, label. */
  id: string;
  /** Watcher key (`MNQZ6.CME`), or the contract when no watcher streams it. */
  symbol: string;
  root: string;
  contract: string;
  /** flow-signals kind, verbatim (`absorption`, `sweep`, ...). */
  kind: string;
  label: string;
  /** Aggressor side for trade kinds, book side for book_imbalance / liquidity. */
  side: string;
  price: number;
  priceTicks: number;
  /** Exchange time, ms. */
  timestamp: number;
  value: number;
  /** Verbatim from flow-signals; may carry `edge`. */
  detail: Record<string, unknown> | null;
}

export interface FlowSymbolStatus {
  contract: string;
  dataLagMs: number | null;
  signals: number;
}

export interface FlowStatus {
  enabled: boolean;
  connected: boolean;
  url: string;
  lastMessageMs: number | null;
  connects: number;
  symbols: Record<string, FlowSymbolStatus>;
}

/** Newest signals kept per root. */
export const MAX_FLOW_SIGNALS = 20_000;
/** History requested on load: the engine keeps 36h. */
export const FLOW_HISTORY_MS = 36 * 3_600_000;
/** Live signals are applied in batches this far apart, ms. */
export const FLOW_FLUSH_MS = 200;

// ------------------------------------------------------------- toggles

export type FlowLevelKey =
  | 'flowAbsorption'
  | 'flowSweep'
  | 'flowStopRun'
  | 'flowStopVolume'
  | 'flowVolumeSpike'
  | 'flowImbalance'
  | 'flowIceberg'
  | 'flowBookImbalance'
  | 'flowLiquidity'
  | 'flowSweepFollow'
  | 'flowBurstLevel'
  | 'flowAbsBreakLevel'
  | 'flowQueueDepletion';

/** The levelsStore toggle that shows a kind; null = never drawn. */
const KIND_TOGGLE: Record<string, FlowLevelKey> = {
  absorption: 'flowAbsorption',
  sweep: 'flowSweep',
  passive_sweep_trace: 'flowSweep',
  stop_sweep_trace: 'flowSweep',
  stop_run: 'flowStopRun',
  stop_volume: 'flowStopVolume',
  volume_spike: 'flowVolumeSpike',
  imbalance_signal: 'flowImbalance',
  iceberg: 'flowIceberg',
  iceberg_development: 'flowIceberg',
  iceberg_cancelled: 'flowIceberg',
  book_imbalance: 'flowBookImbalance',
  liquidity: 'flowLiquidity',
  sweep_follow: 'flowSweepFollow',
  burst_level: 'flowBurstLevel',
  abs_break_level: 'flowAbsBreakLevel',
  queue_depletion: 'flowQueueDepletion',
};

export function flowToggleKey(kind: string): FlowLevelKey | null {
  return KIND_TOGGLE[kind] ?? null;
}

/** Every flow toggle, in Levels-panel order. */
export const FLOW_LEVEL_KEYS: readonly FlowLevelKey[] = [
  'flowAbsorption', 'flowSweep', 'flowStopRun', 'flowStopVolume', 'flowVolumeSpike',
  'flowImbalance', 'flowIceberg', 'flowBookImbalance', 'flowLiquidity', 'flowSweepFollow',
  'flowBurstLevel', 'flowAbsBreakLevel', 'flowQueueDepletion',
];

/**
 * Context kinds: research-backed reads (held-out stats in the Signals tab)
 * rather than tuned triggers (sweep_follow is report-only in the tuner, so any
 * `detail.edge` is informational): the "dim kinds without a reliable edge" rule
 * and the alert edge gate do not apply to them.
 */
export const FLOW_CONTEXT_KINDS: ReadonlySet<string> = new Set([
  'sweep_follow', 'burst_level', 'abs_break_level', 'queue_depletion',
]);

/**
 * MBO research context markers (flow-signals `mbo_context.rs`): display only.
 * Never alertable (not in ALERT_KINDS, engine ALERTABLE_KINDS), so no push
 * and no chime; drawn with the context timer ring over `detail.hold_s`.
 */
export const MBO_CONTEXT_KINDS = ['burst_level', 'abs_break_level', 'queue_depletion'] as const;
export type MboContextKind = (typeof MBO_CONTEXT_KINDS)[number];

export function isMboContextKind(kind: string): kind is MboContextKind {
  return (MBO_CONTEXT_KINDS as readonly string[]).includes(kind);
}

/**
 * What 2024-2025 MBO history measured after each marker, per root
 * (/mnt/Fast/mbo-research/report.md "Full history"; ticks over the next 60 s
 * in the marker's direction). Facts about the past sample, not settings.
 */
export const MBO_CONTEXT_EFFECT: Record<MboContextKind, Partial<Record<string, string>>> = {
  burst_level: { NQ: '+1.1 ticks', MNQ: '+1.5 ticks' },
  abs_break_level: { NQ: '+1.0 tick', MNQ: '+1.5 ticks' },
  queue_depletion: { ES: '+0.7 ticks', MES: '+0.6 ticks', NQ: '+1.4 ticks', MNQ: '+1.2 ticks' },
};

const MBO_CONTEXT_NAME: Record<MboContextKind, string> = {
  burst_level: 'Burst into level',
  abs_break_level: 'Absorption break at level',
  queue_depletion: 'Queue depletion',
};

/** Root of a signal: `s.root`, else the symbol's base. */
function signalRoot(s: Pick<FlowSignal, 'root' | 'symbol'>): string {
  if (s.root) return s.root;
  const base = (s.symbol || '').split('.')[0];
  return base.replace(/[FGHJKMNQUVXZ]\d{1,2}$/, '');
}

/**
 * One plain-words line for a context marker: what happened, what history
 * says came next, and that it is context. E.g. "Burst into HOD (buyers): break
 * likely, don't fade. NQ history: +1.1 ticks over the next 60 s in the push's
 * direction. Context, not a signal."
 */
export function mboContextText(s: Pick<FlowSignal, 'kind' | 'side' | 'root' | 'symbol' | 'detail'>): string {
  if (!isMboContextKind(s.kind)) return '';
  const d = s.detail ?? {};
  const root = signalRoot(s);
  const hold = typeof d.hold_s === 'number' && d.hold_s > 0 ? d.hold_s : 60;
  const level = typeof d.level === 'string' ? d.level : null;
  const effect = MBO_CONTEXT_EFFECT[s.kind][root];
  const up = s.side === 'buy';
  let head: string;
  let dirWord: string;
  if (s.kind === 'burst_level') {
    head = `Burst into ${level ?? 'a level'} (${up ? 'buyers' : 'sellers'}): break likely, don't fade`;
    dirWord = "the push's direction";
  } else if (s.kind === 'abs_break_level') {
    head = `Absorption broke ${up ? 'up' : 'down'}${level ? ` near ${level}` : ''}: don't fade the break`;
    dirWord = 'the break direction';
  } else {
    const side = d.queue_side === 'bid' || d.queue_side === 'ask' ? d.queue_side : up ? 'ask' : 'bid';
    head = `${side === 'ask' ? 'Ask' : 'Bid'} queue emptied: next tick likely ${up ? 'up' : 'down'}`;
    dirWord = 'that direction';
  }
  const hist = effect
    ? ` ${root} history: ${effect} over the next ${hold} s in ${dirWord}.`
    : ` Not measured on ${root || 'this root'}.`;
  return `${head}.${hist} Context, not a signal.`;
}

/** Timer-ring length of a context kind, s (detail.hold_s, else 60); null = no ring. */
export function contextHoldS(s: Pick<FlowSignal, 'kind' | 'detail'>): number | null {
  if (!isFlowContextKind(s.kind)) return null;
  return sweepFollowHoldS(s.detail);
}

export function isFlowContextKind(kind: string): boolean {
  return FLOW_CONTEXT_KINDS.has(kind);
}

/** sweep_follow: momentum is expected to continue this long after the signal, s (detail.hold_s). */
export const SWEEP_FOLLOW_HOLD_S = 60;

/** detail.hold_s of a sweep_follow, else SWEEP_FOLLOW_HOLD_S. */
export function sweepFollowHoldS(detail: FlowSignal['detail']): number {
  const v = detail?.hold_s;
  return typeof v === 'number' && Number.isFinite(v) && v > 0 ? v : SWEEP_FOLLOW_HOLD_S;
}

/**
 * "Buy sweep follow-through: 6 levels, +2t in 1.2s. Momentum likely continues
 * ~60 s (context, not an entry)". The extension is measured from the sweep's
 * own extreme (`sweep_ext_price`) to the signal print, in ticks; falls back to
 * `extend_ticks` (the threshold) when the price fields are missing.
 */
export function sweepFollowText(s: Pick<FlowSignal, 'side' | 'price' | 'priceTicks' | 'detail'>): string {
  const d = s.detail ?? {};
  const n = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);
  const side = s.side === 'buy' ? 'Buy' : s.side === 'sell' ? 'Sell' : s.side;
  const parts: string[] = [];
  const levels = n(d.levels);
  if (levels !== null) parts.push(`${Math.abs(Math.round(levels))} levels`);
  const ext = n(d.sweep_ext_price);
  // Tick size from the signal itself (price / priceTicks), so no instrument table.
  const tick = s.priceTicks > 0 && s.price > 0 ? s.price / s.priceTicks : null;
  let ticks = ext !== null && tick ? Math.round(Math.abs(s.price - ext) / tick) : null;
  if (ticks === null || ticks <= 0) ticks = n(d.extend_ticks);
  const delay = n(d.delay_ms);
  if (ticks !== null) parts.push(`+${ticks}t${delay !== null ? ` in ${(delay / 1000).toFixed(1)}s` : ''}`);
  const head = `${side} sweep follow-through${parts.length > 0 ? `: ${parts.join(', ')}` : ''}`;
  return `${head}. Unproven: on 2024-2025 history the move came in the first 5 s and then faded (context only)`;
}

/** levelsStore master switch for every TradeFinder marker. */
export const FLOW_MASTER_KEY = 'flowMarkers' as const;

/** Relay health for the TradeFinder button's dot. */
export type FlowStatusDot = 'connected' | 'offline' | 'disabled' | 'unknown';

export function flowStatusDot(status: FlowStatus | null): FlowStatusDot {
  if (!status) return 'unknown';
  if (!status.enabled) return 'disabled';
  return status.connected ? 'connected' : 'offline';
}

export function fmtFlowNumber(v: number): string {
  return Math.abs(v) >= 100 ? Math.round(v).toLocaleString('en-US') : String(Math.round(v * 100) / 100);
}

export function fmtFlowTime(ts: number): string {
  return new Date(ts).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
}

// ------------------------------------------------------------- reading

export type FlowReading = 'bullish' | 'bearish';

/**
 * Trading reading of a signal (PROTOCOL.md "Kinds"): absorption is the
 * opposite of its aggressor, icebergs read by the hidden order's side, book
 * kinds by book side, everything else with the aggressor.
 */
export function flowReading(s: Pick<FlowSignal, 'kind' | 'side' | 'label' | 'value'>): FlowReading {
  const buy = s.side === 'buy';
  if (s.kind === 'absorption') return buy ? 'bearish' : 'bullish';
  if (s.kind.startsWith('iceberg')) {
    if (s.label.startsWith('Iceberg Bid')) return 'bullish';
    if (s.label.startsWith('Iceberg Ask')) return 'bearish';
    // Iceberg Bid = sellers hitting a hidden buyer: side sell.
    return buy ? 'bearish' : 'bullish';
  }
  return buy ? 'bullish' : 'bearish';
}

// ------------------------------------------------------------- payloads

export function isFlowSignal(m: unknown): m is FlowSignal {
  if (!m || typeof m !== 'object') return false;
  const c = m as Partial<FlowSignal>;
  return (
    typeof c.id === 'string' &&
    typeof c.symbol === 'string' &&
    typeof c.kind === 'string' &&
    typeof c.side === 'string' &&
    typeof c.price === 'number' && Number.isFinite(c.price) &&
    typeof c.timestamp === 'number' && Number.isFinite(c.timestamp)
  );
}

/** Coerce the optional fields so the rest of the app can trust the shape. */
function clean(s: FlowSignal): FlowSignal {
  return {
    ...s,
    root: typeof s.root === 'string' ? s.root : '',
    contract: typeof s.contract === 'string' ? s.contract : '',
    label: typeof s.label === 'string' ? s.label : '',
    priceTicks: typeof s.priceTicks === 'number' ? s.priceTicks : 0,
    value: typeof s.value === 'number' && Number.isFinite(s.value) ? s.value : 0,
    detail: s.detail && typeof s.detail === 'object' ? s.detail : null,
  };
}

export function isFlowStatus(m: unknown): m is FlowStatus {
  if (!m || typeof m !== 'object') return false;
  const c = m as Partial<FlowStatus>;
  return typeof c.enabled === 'boolean' && typeof c.connected === 'boolean';
}

const cap = (list: FlowSignal[]): FlowSignal[] =>
  list.length > MAX_FLOW_SIGNALS ? list.slice(list.length - MAX_FLOW_SIGNALS) : list;

/**
 * Upsert by id, keep ascending by time, cap (newest kept). Live signals are
 * nearly always newer than everything held, so that case appends without
 * re-sorting the whole list.
 */
export function mergeFlowSignals(existing: FlowSignal[], incoming: readonly unknown[]): FlowSignal[] {
  const fresh = new Map<string, FlowSignal>();
  for (const m of incoming) if (isFlowSignal(m)) fresh.set(m.id, clean(m));
  if (fresh.size === 0) return existing;
  const add = [...fresh.values()].sort((a, b) => a.timestamp - b.timestamp);
  const minTs = add[0].timestamp;
  // A duplicate carries its original timestamp, so it can only sit in the tail.
  let tailStart = existing.length;
  while (tailStart > 0 && existing[tailStart - 1].timestamp >= minTs) tailStart -= 1;
  const tail = existing.slice(tailStart);
  const lastTs = existing.length > 0 ? existing[existing.length - 1].timestamp : -Infinity;
  if (minTs >= lastTs && !tail.some((s) => fresh.has(s.id))) return cap(existing.concat(add));
  const byId = new Map(tail.map((s) => [s.id, s]));
  for (const s of add) byId.set(s.id, s);
  const merged = [...byId.values()].sort((a, b) => a.timestamp - b.timestamp);
  return cap(existing.slice(0, tailStart).concat(merged));
}

// ------------------------------------------------------------- tooltip


const num = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? v : null);

/** One decimal with a real minus sign: -3.14 -> "−3.1". */
export function fmtSigned1(v: number): string {
  const r = Math.round(v * 10) / 10;
  return (r < 0 ? '\u2212' : '') + Math.abs(r).toFixed(1);
}

/** 120 -> "2m", 90 -> "90s". */
export function fmtHorizon(seconds: number): string {
  return seconds >= 60 && seconds % 60 === 0 ? `${seconds / 60}m` : `${seconds}s`;
}

export interface FlowEdgeReadout {
  /** "reversal · 2m −3.1t · band t −2.4". */
  text: string;
  /** |band_t| (or the 300 s t for old tuner files) at or above EDGE_T_RELIABLE. */
  reliable: boolean;
  /** True when built from mean_ticks_300s / t because the file has no band_t. */
  legacy: boolean;
}

/**
 * The tuner's edge for a signal, honestly: based on band_t (the 1-5 min band
 * the tuner selects on) and the mean at its best horizon; marked weak below
 * EDGE_T_RELIABLE. Tuner files without band_t fall back to the 300 s mean / t.
 */
export function flowEdge(detail: FlowSignal['detail']): FlowEdgeReadout | null {
  const e = detail?.edge as Partial<FlowEdge> | undefined;
  if (!e || typeof e !== 'object' || typeof e.reading !== 'string') return null;
  const bandT = num(e.band_t);
  const parts = [e.reading];
  let t: number | null;
  let legacy = false;
  if (bandT !== null) {
    const best = num(e.best_horizon_s);
    const means = e.mean_ticks && typeof e.mean_ticks === 'object' ? e.mean_ticks : {};
    const bestMean = best !== null ? num((means as Record<string, unknown>)[String(best)]) : null;
    if (best !== null && bestMean !== null) parts.push(`${fmtHorizon(best)} ${fmtSigned1(bestMean)}t`);
    else if (num(e.mean_ticks_300s) !== null) parts.push(`5m ${fmtSigned1(e.mean_ticks_300s as number)}t`);
    parts.push(`band t ${fmtSigned1(bandT)}`);
    t = bandT;
  } else {
    legacy = true;
    t = num(e.t);
    if (num(e.mean_ticks_300s) !== null) parts.push(`5m ${fmtSigned1(e.mean_ticks_300s as number)}t`);
    if (t !== null) parts.push(`t ${fmtSigned1(t)}`);
  }
  const reliable = t !== null && Math.abs(t) >= EDGE_T_RELIABLE;
  return { text: parts.join(' \u00b7 '), reliable, legacy };
}

/**
 * "edge: reversal · 2m −3.1t · band t −2.4", or "no reliable edge: …" below
 * EDGE_T_RELIABLE; null when the service has no reading for this group.
 */
export function flowEdgeLine(detail: FlowSignal['detail']): string | null {
  const r = flowEdge(detail);
  if (!r) return null;
  return `${r.reliable ? 'edge' : 'no reliable edge'}: ${r.text}`;
}

/** Tooltip title + lines for one signal. */
export function flowTooltip(s: FlowSignal): { title: string; lines: string[] } {
  const time = fmtFlowTime(s.timestamp);
  const reading = flowReading(s);
  const lines = [
    `value ${fmtFlowNumber(s.value)}`,
    `price ${s.price.toFixed(2)}`,
    time,
    `${reading} (${reading === 'bullish' ? 'below' : 'above'} the price)`,
  ];
  if (s.kind === 'sweep_follow') {
    lines.unshift(sweepFollowText(s));
    const tod = typeof s.detail?.tod === 'string' ? s.detail.tod : null;
    if (tod) lines.push(`${tod} PT`);
  }
  if (isMboContextKind(s.kind)) {
    lines.unshift(mboContextText(s));
    const d = s.detail ?? {};
    if (typeof d.level === 'string' && typeof d.level_price === 'number') {
      lines.push(`${d.level} ${d.level_price.toFixed(2)}`);
    }
  }
  const edge = flowEdgeLine(s.detail);
  if (edge) lines.push(edge);
  return { title: s.label || s.kind, lines };
}

// ------------------------------------------------------------- store

interface FlowSignalsState {
  /** Keyed by base symbol (NQ, MNQ, ES, MES). */
  bySymbol: Record<string, FlowSignal[]>;
  status: FlowStatus | null;

  addSignals: (signals: readonly unknown[]) => void;
  setStatus: (status: FlowStatus) => void;
  fetchSignals: (symbol: string) => Promise<void>;
  fetchStatus: () => Promise<void>;
  /** A chart shows `symbol`: load its history now and after every reconnect. */
  watchSymbol: (symbol: string) => () => void;
  /** Reference-counted: every chart showing flow markers calls both. */
  initialize: () => void;
  teardown: () => void;
}

const watched = new Map<string, number>();
let users = 0;
let unsubMarket: (() => void) | null = null;
let unsubRelay: (() => void) | null = null;
let pending: unknown[] = [];
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let warned = false;

const liveListeners = new Set<(signals: FlowSignal[]) => void>();

/**
 * Signals pushed live over the socket (never REST history), per flush batch,
 * after they reached the store. For the strip pulse and the chime.
 */
export function onLiveFlowSignals(cb: (signals: FlowSignal[]) => void): () => void {
  liveListeners.add(cb);
  return () => {
    liveListeners.delete(cb);
  };
}

const flush = (): void => {
  flushTimer = null;
  const batch = pending;
  pending = [];
  if (batch.length === 0) return;
  useFlowSignalsStore.getState().addSignals(batch);
  if (liveListeners.size === 0) return;
  const live = batch.filter(isFlowSignal).map(clean);
  for (const l of [...liveListeners]) {
    try { l(live); } catch (e) { console.warn('[FlowSignals] live listener failed', e); }
  }
};

/**
 * Feed signals as if they arrived on the socket (DEV builds only: the staging
 * Vite server). Playwright uses it when the market is quiet; production
 * builds never define it.
 */
export function injectFlowSignalsForTest(signals: readonly unknown[]): void {
  for (const m of signals) onSignal(m);
  if (flushTimer !== null) clearTimeout(flushTimer);
  flush();
}

if (typeof window !== 'undefined' && import.meta.env?.DEV) {
  (window as unknown as { __uttFlowInject?: (s: unknown[]) => void }).__uttFlowInject = (s) => injectFlowSignalsForTest(s);
}

const onSignal = (m: unknown): void => {
  if (!isFlowSignal(m)) return;
  pending.push(m);
  if (flushTimer === null) flushTimer = setTimeout(flush, FLOW_FLUSH_MS);
};

const onStatus = (m: unknown): void => {
  if (isFlowStatus(m)) useFlowSignalsStore.getState().setStatus(m);
};

/**
 * Calls `cb` whenever the engine's flow-signals relay goes from not connected
 * to connected (flow-status). The engine only pushes signals younger than 60s
 * and the line rooms miss every point while the relay is down, so a longer
 * outage leaves a gap that only REST can fill. The first status after load
 * (null -> connected) is not a flip: watchers load history when they start.
 */
export function onFlowRelayReconnect(cb: () => void): () => void {
  let prev = useFlowSignalsStore.getState().status?.connected ?? null;
  return useFlowSignalsStore.subscribe((state) => {
    const now = state.status?.connected ?? null;
    if (now === prev) return;
    const back = prev === false && now === true;
    prev = now;
    if (back) cb();
  });
}

/**
 * Two gap triggers that fire together must cost one refetch: a refresh this
 * soon after the previous one is skipped (the earlier one already read
 * everything the engine had).
 */
export const FLOW_GAP_DEDUPE_MS = 2_000;
/** After a socket reconnect, how long to wait for the relay status before refetching anyway. */
export const FLOW_STATUS_WAIT_MS = 3_000;

const gapListeners = new Set<() => void>();
let gapStop: (() => void) | null = null;
let lastGapAt = -Infinity;

function emitGap(): void {
  const t = Date.now();
  if (t - lastGapAt < FLOW_GAP_DEDUPE_MS) return;
  lastGapAt = t;
  for (const l of [...gapListeners]) l();
}

/**
 * One history gap per reconnect transition, for every store that backfills
 * over REST (signals, lines). A gap is:
 * - the socket coming back: once the first fresh flow-status says the relay
 *   is up (or the status cannot be read: refetch anyway). When it says the
 *   relay is still down, nothing yet: the relay's own flip below covers it,
 *   with the history the relay has caught up on by then.
 * - the relay flipping offline -> connected while the socket stayed up.
 * An engine restart is both (socket back, relay down, relay up) and used to
 * refetch twice; now it refetches once, when the relay is up.
 */
function startGapWatch(): () => void {
  let prevConn = useMarketStore.getState().connectionStatus;
  let prevRelay = useFlowSignalsStore.getState().status?.connected ?? null;
  let awaitingStatus = false;
  let socketBacks = 0;
  let statusTimer: ReturnType<typeof setTimeout> | null = null;
  const unsubMarket = useMarketStore.subscribe((state) => {
    const now = state.connectionStatus;
    if (now === prevConn) return;
    prevConn = now;
    if (now !== 'connected') return;
    awaitingStatus = true;
    const token = (socketBacks += 1);
    // No fresh status (engine unreachable, one without flow-status, or a
    // status request that hangs): refetch anyway rather than leave the gap.
    const giveUp = () => {
      if (!awaitingStatus || token !== socketBacks) return;
      if (useMarketStore.getState().connectionStatus !== 'connected') return; // down again: the next reconnect handles it
      awaitingStatus = false;
      emitGap();
    };
    if (statusTimer !== null) clearTimeout(statusTimer);
    statusTimer = setTimeout(() => {
      statusTimer = null;
      giveUp();
    }, FLOW_STATUS_WAIT_MS);
    void useFlowSignalsStore.getState().fetchStatus().finally(giveUp);
  });
  const unsubStatus = useFlowSignalsStore.subscribe((state, prevState) => {
    if (state.status === prevState.status) return;
    const now = state.status?.connected ?? null;
    const was = prevRelay;
    prevRelay = now;
    if (awaitingStatus) {
      awaitingStatus = false;
      if (now === true) emitGap();
      return;
    }
    if (was === false && now === true) emitGap();
  });
  return () => {
    unsubMarket();
    unsubStatus();
    if (statusTimer !== null) clearTimeout(statusTimer);
    statusTimer = null;
  };
}

/** Subscribe to history gaps (see startGapWatch); reference-counted. */
export function onFlowHistoryGap(cb: () => void): () => void {
  gapListeners.add(cb);
  if (gapListeners.size === 1 && !gapStop) gapStop = startGapWatch();
  let done = false;
  return () => {
    if (done) return;
    done = true;
    gapListeners.delete(cb);
    if (gapListeners.size === 0 && gapStop) {
      gapStop();
      gapStop = null;
    }
  };
}

/** Tests: forget the last refresh time. */
export function resetFlowGapDedupe(): void {
  lastGapAt = -Infinity;
}

/** (Re)bind the socket handlers: a forced reconnect builds a new socket. */
function bindSocket(): void {
  if (!wsService.isConnected()) return; // bound on the next 'connected' instead
  wsService.off('flow-signal', onSignal);
  wsService.off('flow-status', onStatus);
  wsService.on('flow-signal', onSignal);
  wsService.on('flow-status', onStatus);
}

export const useFlowSignalsStore = create<FlowSignalsState>((set, get) => ({
  bySymbol: {},
  status: null,

  addSignals: (signals) =>
    set((s) => {
      const groups = new Map<string, unknown[]>();
      for (const m of signals) {
        if (!isFlowSignal(m)) continue;
        const base = normalizeToBase(m.symbol);
        const g = groups.get(base);
        if (g) g.push(m);
        else groups.set(base, [m]);
      }
      if (groups.size === 0) return s;
      const next = { ...s.bySymbol };
      for (const [base, list] of groups) next[base] = mergeFlowSignals(next[base] ?? [], list);
      return { bySymbol: next };
    }),

  setStatus: (status) => set({ status }),

  fetchSignals: async (symbol) => {
    if (!symbol) return;
    const since = Date.now() - FLOW_HISTORY_MS;
    try {
      const res = await fetch(
        `${getApiBaseUrl()}/flow-signals/${encodeURIComponent(symbol)}?since_ms=${since}&limit=${MAX_FLOW_SIGNALS}`,
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const body = await res.json();
      const signals: unknown[] = Array.isArray(body?.signals) ? body.signals : [];
      // Merge, not replace: a live signal may have landed while this was in flight.
      const base = normalizeToBase(symbol);
      set((s) => ({ bySymbol: { ...s.bySymbol, [base]: mergeFlowSignals(s.bySymbol[base] ?? [], signals) } }));
    } catch (e) {
      if (!warned) {
        warned = true;
        console.warn('[FlowSignals] history unavailable (engine not deployed yet?)', e);
      }
    }
  },

  fetchStatus: async () => {
    try {
      const res = await fetch(`${getApiBaseUrl()}/flow-status`);
      if (!res.ok) return;
      const body = await res.json();
      if (isFlowStatus(body)) set({ status: body });
    } catch {
      /* engine not reachable: the next broadcast fills it in */
    }
  },

  watchSymbol: (symbol) => {
    if (!symbol) return () => {};
    watched.set(symbol, (watched.get(symbol) ?? 0) + 1);
    void get().fetchSignals(symbol);
    let done = false;
    return () => {
      if (done) return;
      done = true;
      const n = (watched.get(symbol) ?? 1) - 1;
      if (n <= 0) watched.delete(symbol);
      else watched.set(symbol, n);
    };
  },

  initialize: () => {
    users += 1;
    if (users > 1) return;
    bindSocket();
    void get().fetchStatus();
    let prev = useMarketStore.getState().connectionStatus;
    unsubMarket = useMarketStore.subscribe((state) => {
      const now = state.connectionStatus;
      if (now === prev) return;
      prev = now;
      // A forced reconnect builds a new socket: rebind. (The status fetch and
      // the history refetch are the gap watch's.)
      if (now === 'connected') bindSocket();
    });
    // Anything older than 60s is never pushed, so a gap comes over REST:
    // once per socket / relay reconnect.
    unsubRelay = onFlowHistoryGap(() => {
      for (const sym of watched.keys()) void get().fetchSignals(sym);
    });
  },

  teardown: () => {
    if (users === 0) return;
    users -= 1;
    if (users > 0) return;
    unsubMarket?.();
    unsubMarket = null;
    unsubRelay?.();
    unsubRelay = null;
    if (flushTimer !== null) clearTimeout(flushTimer);
    flushTimer = null;
    pending = [];
    wsService.off('flow-signal', onSignal);
    wsService.off('flow-status', onStatus);
  },
}));
